import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { requestJira } from '../shims/forge-bridge';

export interface JiraUser {
  accountId: string;
  displayName: string;
  emailAddress?: string;
  avatarUrls?: {
    '48x48': string;
    '24x24': string;
    '16x16': string;
    '32x32': string;
  };
}

interface UserContextValue {
  user: JiraUser | null;
  userInitials: string;
  isLoading: boolean;
}

const UserContext = createContext<UserContextValue>({
  user: null,
  userInitials: 'U',
  isLoading: true
});

/**
 * Extracts initials from a display name
 * e.g., "John Smith" -> "JS", "Alice" -> "A", "John Paul Smith" -> "JP"
 */
const getInitials = (displayName: string): string => {
  if (!displayName) return 'U';
  
  const parts = displayName.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  
  // Get first letter of first name and first letter of last name
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<JiraUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await requestJira('/rest/api/3/myself', {
          method: 'GET',
          headers: {
            Accept: 'application/json'
          }
        });
        
        const userData: JiraUser = await response.json();
        setUser(userData);
      } catch (error) {
        console.error('Failed to fetch current user:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  const userInitials = user?.displayName ? getInitials(user.displayName) : 'U';

  return (
    <UserContext.Provider value={{ user, userInitials, isLoading }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
